import { useMemo, useState } from "react";
import { SortOrder, TABLE_ACTIONS } from "helpers/constants";
import CustomTable from "components/Table/CustomTable";
import {
  Box,
  Chip,
  IconButton,
  Link as LinkBase,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { CoachPetAvatar } from "components/VetStore/CoachPetAvatar";
import { DomainColorSquares } from "components/Requirements";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { Link } from "react-router-dom";
import { OpenInNew, Info } from "@mui/icons-material";
import { ROLES } from "types/user";
import dayjs from "dayjs";
import { FeedbackStatusChip } from "components/Feedback";
import { SubRequirementsModal } from "components/Feedback/SubRequirementsModal";
import {
  getFeedbackDetailPath,
  getFeedbackStatus,
  shouldHideFeedback,
} from "utils/feedback";
import {
  getFeedbackTypeLabel,
  getRequirementDisplayData,
} from "utils/feedbackIter";
import { Requirement } from "types/requirement";

interface FeedbackTableProps {
  feedbacks: FeedbackType[];
  handleActionClick: (action: TABLE_ACTIONS, id: string) => void;
  userRole?: string;
  status?: FeedbackStatus;
  excludeDraft?: boolean;
  allRequirements?: Requirement[];
  /** When set, feedback "View" links use this base path (e.g. `/organization/:orgId/feedback`) */
  feedbackDetailBasePath?: string;
  /** When true, no row actions menu is shown */
  readOnly?: boolean;
}

export const FeedbackTable = ({
  feedbacks,
  handleActionClick,
  userRole,
  status,
  excludeDraft,
  allRequirements = [],
  feedbackDetailBasePath,
  readOnly = false,
}: FeedbackTableProps) => {
  const isCoach = userRole === ROLES.COACH;
  const feedbackDetailPath = (feedbackId: string) =>
    feedbackDetailBasePath
      ? `${feedbackDetailBasePath}/${feedbackId}`
      : getFeedbackDetailPath(feedbackId, userRole);
  const [showHidden, setShowHidden] = useState(false);
  const [openSubRequirementsModal, setOpenSubRequirementsModal] = useState(false);
  const [selectedIterData, setSelectedIterData] = useState<{
    iterName: string;
    subRequirements: Array<{ requirement_id: string; name: string; description?: string | null }>;
    ratingsByRequirement?: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    >;
  } | null>(null);

  // Build ratings by requirement for ITER competencies
  const buildRatingsByRequirementForIter = (iterName: string) => {
    const ratingsByRequirement: Record<
      string,
      {
        date: string;
        learnerRating?: number | null;
        coachRating?: number | null;
      }[]
    > = {};

    const history = feedbacks
      .filter((fb) => {
        const data = getRequirementDisplayData(fb, allRequirements);
        return data.isITER && data.name === iterName;
      })
      .sort((a, b) => {
        const da = a.observation_date || a.created_at || "";
        const db = b.observation_date || b.created_at || "";
        return da.localeCompare(db);
      });

    history.forEach((fb) => {
      const date = (fb.observation_date || fb.created_at) as string;
      fb.feedback_skills.forEach((skill) => {
        const reqId = skill.requirement?.requirement_id;
        if (!reqId) return;
        if (!ratingsByRequirement[reqId]) {
          ratingsByRequirement[reqId] = [];
        }
        ratingsByRequirement[reqId].push({
          date,
          learnerRating: skill.self_rating ?? null,
          coachRating: skill.coach_rating ?? null,
        });
      });
    });

    return ratingsByRequirement;
  };

  const getTypeLabel = (feedback: any): string =>
    getFeedbackTypeLabel(feedback, allRequirements);

  // Helper function to get type chip for a feedback
  const getTypeChip = (feedback: any) => {
    const label = getTypeLabel(feedback);
    if (!label) return null;

    let color: "primary" | "success" | "info" | "warning" = "primary";
    if (label === "EPA") {
      color = "primary";
    } else if (label === "Competencies") {
      color = "success";
    } else if (label === "ITER") {
      color = "info";
    }

    return <Chip label={label} size="small" color={color} />;
  };

  // Custom status ordering
  const statusOrder: Record<string, number> = {
    [FeedbackStatus.PENDING]: 1,
    [FeedbackStatus.MISSING_INFO]: 2,
    [FeedbackStatus.DONE]: 3,
    [FeedbackStatus.EXPIRED]: 4,
    [FeedbackStatus.DECLINED]: 5,
  };
  const getStatusRank = (status?: string) => statusOrder[status || ""] ?? 6;

  const getActions = (data: any) => {
    const resolvedStatus = getFeedbackStatus(data as FeedbackType);

    if (resolvedStatus === FeedbackStatus.MISSING_INFO) {
      return [TABLE_ACTIONS.EDIT, TABLE_ACTIONS.ARCHIVE, TABLE_ACTIONS.DELETE];
    }
    if (
      resolvedStatus === FeedbackStatus.EXPIRED ||
      resolvedStatus === FeedbackStatus.DECLINED
    ) {
      return [TABLE_ACTIONS.DELETE];
    }
    return [TABLE_ACTIONS.ARCHIVE, TABLE_ACTIONS.DELETE];
  };

  const actions = useMemo(
    () =>
      status === FeedbackStatus.DRAFT || status === FeedbackStatus.MISSING_INFO
        ? [TABLE_ACTIONS.EDIT, TABLE_ACTIONS.ARCHIVE]
        : getActions,
    [status],
  );

  // Filter feedbacks by status or exclude DRAFT
  const filteredFeedbacks = useMemo(() => {
    const byVisibility = feedbacks.filter((feedback) => {
      if (showHidden) return true;
      return (
        feedback.status === FeedbackStatus.DRAFT ||
        !shouldHideFeedback(feedback)
      );
    });
    if (excludeDraft) {
      return byVisibility.filter(
        (feedback) => feedback.status !== FeedbackStatus.DRAFT,
      );
    }
    if (!status) return byVisibility;
    return byVisibility.filter((feedback) => feedback.status === status);
  }, [feedbacks, status, excludeDraft, showHidden]);

  const handleFilter = (row: any, filterText: string) => {
    const filterLower = filterText.toLowerCase();
    
    // Filter by requirement names
    const matchesRequirement = row.feedback_skills?.some((skill: any) =>
      skill.requirement?.name?.toLowerCase().includes(filterLower),
    );
    
    // Filter by student name (for coaches) or coach name (for students)
    const matchesName = isCoach
      ? row.student?.name?.toLowerCase().includes(filterLower)
      : row.coach?.name?.toLowerCase().includes(filterLower);
    
    // Filter by type (EPA, ITER, Competencies)
    const typeLabel = getTypeLabel(row);
    const matchesType = typeLabel.toLowerCase().includes(filterLower);
    
    // Filter by observation date
    let matchesObservationDate = false;
    if (row.observation_date) {
      const observationDate = dayjs(row.observation_date);
      const dateFormats = [
        observationDate.format("MMM D, YYYY"), // "Jan 28, 2026"
        observationDate.format("MMM DD, YYYY"), // "Jan 28, 2026"
        observationDate.format("MMMM D, YYYY"), // "January 28, 2026"
        observationDate.format("MMMM DD, YYYY"), // "January 28, 2026"
        observationDate.format("MM/DD/YYYY"), // "01/28/2026"
        observationDate.format("MM-DD-YYYY"), // "01-28-2026"
        observationDate.format("YYYY-MM-DD"), // "2026-01-28"
        observationDate.format("YYYY/MM/DD"), // "2026/01/28"
        observationDate.format("D MMM YYYY"), // "28 Jan 2026"
        observationDate.format("DD MMM YYYY"), // "28 Jan 2026"
        observationDate.format("YYYY"), // "2026"
        observationDate.format("MMM YYYY"), // "Jan 2026"
        observationDate.format("MMMM YYYY"), // "January 2026"
      ];
      matchesObservationDate = dateFormats.some((format) =>
        format.toLowerCase().includes(filterLower),
      );
    }
    
    // Filter by created date
    let matchesCreatedDate = false;
    if (row.created_at) {
      const createdDate = dayjs(row.created_at);
      const dateFormats = [
        createdDate.format("MMM D, YYYY"),
        createdDate.format("MMM DD, YYYY"),
        createdDate.format("MMMM D, YYYY"),
        createdDate.format("MMMM DD, YYYY"),
        createdDate.format("MM/DD/YYYY"),
        createdDate.format("MM-DD-YYYY"),
        createdDate.format("YYYY-MM-DD"),
        createdDate.format("YYYY/MM/DD"),
        createdDate.format("D MMM YYYY"),
        createdDate.format("DD MMM YYYY"),
        createdDate.format("YYYY"),
        createdDate.format("MMM YYYY"),
        createdDate.format("MMMM YYYY"),
      ];
      matchesCreatedDate = dateFormats.some((format) =>
        format.toLowerCase().includes(filterLower),
      );
    }
    
    return (
      matchesRequirement ||
      matchesName ||
      matchesType ||
      matchesObservationDate ||
      matchesCreatedDate
    );
  };

  const columns = useMemo(() => {
    let columnsBase: any[] = [
      {
        id: "feedback_skills",
        label: "Requirements",
        value: (row: any) => {
          if (row.feedback_skills.length === 0) {
            return "No requirements";
          }
          const requirementData = getRequirementDisplayData(row, allRequirements);
          return (
            <Stack spacing={0.5} alignItems="flex-start">
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2">{requirementData.name}</Typography>
                {requirementData.isITER && requirementData.iterData && (
                  <Tooltip title="View Competencies">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedIterData({
                          ...requirementData.iterData,
                          ratingsByRequirement: buildRatingsByRequirementForIter(
                            requirementData.name as string,
                          ),
                        });
                        setOpenSubRequirementsModal(true);
                      }}
                    >
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <DomainColorSquares
                requirement={row.feedback_skills[0]?.requirement}
              />
            </Stack>
          );
        },
        sort: (row: any) => {
          if (row.feedback_skills.length === 0) {
            return "";
          }
          const requirementData = getRequirementDisplayData(row, allRequirements);
          return requirementData.name || "";
        },
      },
      {
        id: "type",
        label: "Type",
        value: (row: any) => getTypeChip(row),
        sort: (row: any) => getTypeLabel(row),
      },
      {
        id: "student",
        label: "Learner",
        value: (row: any) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <CoachPetAvatar
              pictureUrl={row.student?.picture_url}
              name={row.student?.name}
              equippedStoreItems={row.student?.equipped_store_items}
              avatarSize={28}
            />
            <Typography variant="body2">{row.student?.name || "-"}</Typography>
          </Stack>
        ),
        sort: (row: any) => row.student?.name || "",
      },
      {
        id: "coach",
        label: "Coach",
        value: (row: any) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <CoachPetAvatar
              pictureUrl={row.coach?.picture_url}
              name={row.coach?.name}
              equippedStoreItems={row.coach?.equipped_store_items}
              avatarSize={28}
            />
            <Typography variant="body2">{row.coach?.name || "-"}</Typography>
          </Stack>
        ),
        sort: (row: any) => row.coach?.name || "",
      },
    ];

    // Add Organization column only for COACH users
    if (isCoach) {
      columnsBase.push({
        id: "organization",
        label: "Organization",
        value: (row: any) => row.organization?.name || "N/A",
        sort: (row: any) => row.organization?.name || "",
      });
    }

    // Continue with the rest of the columns
    columnsBase.push(
      {
        id: "observation_date",
        label: "Observation Date",
        value: (row: any) => dayjs(row["observation_date"]).format("lll"),
        sort: (row: any) => row.observation_date || "",
      },
      {
        id: "status",
        label: "Status",
        value: (row: any) => <FeedbackStatusChip feedback={row} />,
        sort: (a: FeedbackType, b: FeedbackType) =>
          getStatusRank(getFeedbackStatus(a)) -
          getStatusRank(getFeedbackStatus(b)),
      },
      {
        id: "view_feedback",
        value: (row: any) => (
          <Stack direction="row" spacing={2} alignItems="center">
            <LinkBase
              sx={{ cursor: "pointer" }}
              component={Link}
              to={feedbackDetailPath(row.feedback_id)}
            >
              View
            </LinkBase>

            <Tooltip title="Open in new tab" placement="top">
              <IconButton
                component="a"
                href={feedbackDetailPath(row.feedback_id)}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
              >
                <OpenInNew />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    );

    if (!readOnly) {
      columnsBase.push({ id: "actions", value: "" });
    }

    return columnsBase;
  }, [isCoach, userRole, allRequirements, feedbackDetailBasePath, readOnly]);

  return (
    <>
    <CustomTable
      entities={filteredFeedbacks}
      handleActionClick={handleActionClick}
      fieldToFilter={handleFilter}
      columns={columns}
      isSelectable={false}
      allowedMenuItems={readOnly ? [] : actions}
      enableMenuInSmallDevices={!readOnly}
      extraActionItems={[]}
      idColumn={"feedback_id"}
      sortDefault={SortOrder.desc}
      sortDefaultField={"observation_date"}
      enableCardViewInSmallDevices={true}
      filterOptions={
        status === FeedbackStatus.DRAFT
          ? undefined
          : {
              showHidden,
              onShowHiddenChange: setShowHidden,
            }
      }
      renderCardContent={(row: FeedbackType) => {
        const requirementData = getRequirementDisplayData(row, allRequirements);
        return (
          <Stack spacing={0.5}>
            <Typography variant="body1" fontWeight={600}>
              Requirements:
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1">{requirementData.name}</Typography>
              {requirementData.isITER && requirementData.iterData && (
                <Tooltip title="View Competencies">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedIterData({
                        ...requirementData.iterData,
                        ratingsByRequirement: buildRatingsByRequirementForIter(
                          requirementData.name as string,
                        ),
                      });
                      setOpenSubRequirementsModal(true);
                    }}
                  >
                    <Info fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <DomainColorSquares
              requirement={row.feedback_skills?.[0]?.requirement}
            />
            <Typography variant="body1" fontWeight={600} sx={{ mt: 1 }}>
              Type:
            </Typography>
            <Typography variant="body1">{getTypeChip(row) || "N/A"}</Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body1">
                <strong>Coach:</strong>
              </Typography>
              <CoachPetAvatar
                pictureUrl={row.coach?.picture_url}
                name={row.coach?.name}
                equippedStoreItems={row.coach?.equipped_store_items}
                avatarSize={28}
              />
              <Typography variant="body1">{row.coach?.name || "-"}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body1">
                <strong>Learner:</strong>
              </Typography>
              <CoachPetAvatar
                pictureUrl={row.student?.picture_url}
                name={row.student?.name}
                equippedStoreItems={row.student?.equipped_store_items}
                avatarSize={28}
              />
              <Typography variant="body1">{row.student?.name || "-"}</Typography>
            </Stack>
            {isCoach && (
              <Typography variant="body1">
                <strong>Organization:</strong> {row.organization?.name}
              </Typography>
            )}
            <Typography variant="body1">
              <strong>Observation Date:</strong>{" "}
              {dayjs(row.observation_date).format("lll")}
            </Typography>
            <Typography variant="body1">
              <strong>Status:</strong> <FeedbackStatusChip feedback={row} />
            </Typography>
          </Stack>
        );
      }}
    />
    {selectedIterData && (
      <SubRequirementsModal
        open={openSubRequirementsModal}
        onClose={() => {
          setOpenSubRequirementsModal(false);
          setSelectedIterData(null);
        }}
        iterName={selectedIterData.iterName}
        subRequirements={selectedIterData.subRequirements}
        ratingsByRequirement={selectedIterData.ratingsByRequirement}
      />
    )}
  </>
  );
};
